import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { inr, fdate } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { StatusBadge } from "@/components/pawkt/StatusBadge";
import { FormModal } from "@/components/pawkt/FormModal";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { GraduationCap, Plus, PawPrint } from "lucide-react";
import { ContextPanel } from "@/components/pawkt/ContextPanel";
import { toast } from "sonner";

const PROGRESS = [{ value: "not_started", label: "Not started" }, { value: "in_progress", label: "In progress" }, { value: "improving", label: "Improving" }, { value: "completed", label: "Completed" }];
const SESSION_STATUS = [{ value: "scheduled", label: "Scheduled" }, { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" }, { value: "no_show", label: "No show" }];

function Kpi({ label, value, accent = "slate", testid }) {
  const map = { slate: "text-slate-900", sky: "text-sky-700", amber: "text-amber-600", emerald: "text-emerald-700" };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold tabular ${map[accent]}`}>{value}</p>
    </div>
  );
}

export default function Training() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = hasPerm(user, "training.edit");
  const [pkgOpen, setPkgOpen] = useState(false);
  const [sesOpen, setSesOpen] = useState(false);
  const [progOpen, setProgOpen] = useState(false);
  const [ctx, setCtx] = useState(null);

  const { data: dash } = useQuery({ queryKey: ["train-dash"], queryFn: async () => (await api.get("/training/dashboard")).data });
  const { data: pkgs, isLoading } = useQuery({ queryKey: ["train-pkgs"], queryFn: async () => (await api.get("/training/packages")).data });
  const { data: sessions } = useQuery({ queryKey: ["train-sessions"], queryFn: async () => (await api.get("/training/sessions")).data });
  const { data: outcomes } = useQuery({ queryKey: ["train-outcomes"], queryFn: async () => (await api.get("/training/outcomes")).data });
  const { data: pets } = useQuery({ queryKey: ["pets-min"], queryFn: async () => (await api.get("/pets?limit=200")).data.items });

  const petOpts = (pets || []).map((p) => ({ value: p.id, label: `${p.name} · ${p.breed || p.species || "Pet"}${p.pet_code ? ` · ${p.pet_code}` : ""}${p.owner_name ? ` · ${p.owner_name}` : ""}` }));
  const pkgOpts = (pkgs?.items || []).map((p) => ({ value: p.id, label: `${p.package_type || "Package"} · ${(pets || []).find((x) => x.id === p.pet_id)?.name || ""}` }));
  const petName = (id) => (pets || []).find((p) => p.id === id)?.name || id;

  const mk = (path, close, key) => ({ mutationFn: (v) => api.post(`/training/${path}`, v), onSuccess: () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["train-dash"] }); close(false); toast.success("Saved"); }, onError: (e) => toast.error(apiErr(e)) });
  const createPkg = useMutation(mk("packages", setPkgOpen, ["train-pkgs"]));
  const createSes = useMutation(mk("sessions", setSesOpen, ["train-sessions"]));
  const createProg = useMutation(mk("outcomes", setProgOpen, ["train-outcomes"]));
  const setSesStatus = useMutation({ mutationFn: ({ id, status }) => api.put(`/training/sessions/${id}`, { status }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["train-sessions"] }); qc.invalidateQueries({ queryKey: ["train-dash"] }); toast.success("Updated"); }, onError: (e) => toast.error(apiErr(e)) });

  const d = dash || {};
  const pkgRows = pkgs?.items || [];
  const sesRows = sessions?.items || [];
  const outRows = outcomes?.items || [];

  return (
    <div>
      <PageHeader title="Training" subtitle="Packages, sessions & progress tracking">
        {canEdit && <Button onClick={() => setPkgOpen(true)} data-testid="train-new-package-btn" className="bg-slate-900 text-white gap-1.5"><Plus className="h-4 w-4" /> New Package</Button>}
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="train-dashboard">
        <Kpi label="Active packages" value={d.active_packages ?? 0} accent="sky" testid="train-kpi-packages" />
        <Kpi label="Sessions today" value={d.sessions_today ?? 0} testid="train-kpi-today" />
        <Kpi label="Upcoming" value={d.upcoming_sessions ?? 0} accent="amber" testid="train-kpi-upcoming" />
        <Kpi label="Completed" value={d.completed_sessions ?? 0} accent="emerald" testid="train-kpi-completed" />
        <Kpi label="No-shows" value={d.no_shows ?? 0} testid="train-kpi-noshows" />
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
        <Tabs defaultValue="packages">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="packages" data-testid="train-tab-packages">Packages ({pkgRows.length})</TabsTrigger>
            <TabsTrigger value="sessions" data-testid="train-tab-sessions">Sessions ({sesRows.length})</TabsTrigger>
            <TabsTrigger value="progress" data-testid="train-tab-progress">Progress ({outRows.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="packages" className="mt-4">
            <div className="flex justify-end mb-2">{canEdit && <Button size="sm" variant="outline" onClick={() => setSesOpen(true)} data-testid="train-new-session-btn" className="gap-1"><Plus className="h-3 w-3" /> Session</Button>}</div>
            {isLoading ? <Loading /> : pkgRows.length === 0 ? <Empty icon={GraduationCap} title="No packages" subtitle="Create a training package to begin." /> : (
              <div className="divide-y divide-slate-100" data-testid="train-packages-list">
                {pkgRows.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`train-package-${p.id}`}>
                    <div className="flex-1"><div className="font-medium text-slate-800">{petName(p.pet_id)} · {p.package_type || "Package"}</div><div className="text-xs text-slate-400">{p.sessions_purchased || 0} sessions · {inr(p.price || 0)} · exp {fdate(p.expiry_date)}</div></div>
                    <StatusBadge status={p.status || "active"} />
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="sessions" className="mt-4">
            {sesRows.length === 0 ? <Empty icon={GraduationCap} title="No sessions" subtitle="Schedule a session for a package." /> : (
              <div className="divide-y divide-slate-100" data-testid="train-sessions-list">
                {sesRows.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`train-session-${s.id}`}>
                    <button title="Context" data-testid={`ctx-open-${s.id}`} onClick={() => setCtx({ petId: s.pet_id, vertical: "training" })} className="p-1 rounded-md hover:bg-sky-50 text-sky-600"><PawPrint className="h-4 w-4" /></button>
                    <div className="flex-1"><div className="font-medium text-slate-800">{petName(s.pet_id)}</div><div className="text-xs text-slate-400">{fdate(s.scheduled_at)} · {s.objectives || "No objectives recorded"}</div></div>
                    <StatusBadge status={s.status || "scheduled"} />
                    {canEdit && s.status !== "completed" && <Button size="sm" variant="outline" onClick={() => setSesStatus.mutate({ id: s.id, status: "completed" })} data-testid={`train-complete-${s.id}`} className="h-7 text-xs">Complete</Button>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="progress" className="mt-4">
            <div className="flex justify-end mb-2">{canEdit && <Button size="sm" variant="outline" onClick={() => setProgOpen(true)} data-testid="train-new-progress-btn" className="gap-1"><Plus className="h-3 w-3" /> Progress</Button>}</div>
            {outRows.length === 0 ? <Empty icon={GraduationCap} title="No progress recorded" subtitle="Record training progress per skill category." /> : (
              <div className="divide-y divide-slate-100" data-testid="train-progress-list">
                {outRows.map((o) => (
                  <div key={o.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`train-progress-${o.id}`}>
                    <div className="flex-1"><div className="font-medium text-slate-800 capitalize">{(o.category || "").replace("_", " ")} · {petName(o.pet_id)}</div><div className="text-xs text-slate-400">{o.notes || "No notes"}</div></div>
                    <StatusBadge status={o.level || "not_started"} />
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <FormModal open={pkgOpen} onOpenChange={setPkgOpen} title="New Training Package" testid="train-package-modal"
        fields={[
          { name: "pet_id", label: "Pet", type: "select", options: petOpts, full: true },
          { name: "package_type", label: "Package type", placeholder: "Basic obedience" },
          { name: "sessions_purchased", label: "Sessions", type: "number" },
          { name: "start_date", label: "Start", type: "date" },
          { name: "expiry_date", label: "Expiry", type: "date" },
          { name: "price", label: "Price (₹)", type: "number" },
          { name: "status", label: "Status", type: "select", options: [{ value: "active", label: "Active" }, { value: "completed", label: "Completed" }, { value: "expired", label: "Expired" }] },
        ]} onSubmit={(v) => createPkg.mutateAsync(v)} submitLabel="Create" />

      <FormModal open={sesOpen} onOpenChange={setSesOpen} title="New Session" testid="train-session-modal"
        fields={[
          { name: "pet_id", label: "Pet", type: "select", options: petOpts, full: true },
          { name: "package_id", label: "Package", type: "select", options: pkgOpts, full: true },
          { name: "scheduled_at", label: "Scheduled", type: "datetime-local" },
          { name: "duration", label: "Duration (min)", type: "number" },
          { name: "status", label: "Status", type: "select", options: SESSION_STATUS },
          { name: "objectives", label: "Objectives", full: true },
          { name: "notes", label: "Notes", type: "textarea", full: true },
        ]} onSubmit={(v) => createSes.mutateAsync(v)} submitLabel="Schedule" />

      <FormModal open={progOpen} onOpenChange={setProgOpen} title="Record Progress" testid="train-progress-modal"
        fields={[
          { name: "pet_id", label: "Pet", type: "select", options: petOpts, full: true },
          { name: "category", label: "Category", type: "select", options: ["obedience", "leash", "recall", "socialization", "commands", "toilet_training", "behaviour"].map((v) => ({ value: v, label: v.replace("_", " ") })) },
          { name: "level", label: "Level", type: "select", options: PROGRESS },
          { name: "notes", label: "Trainer notes", type: "textarea", full: true },
        ]} onSubmit={(v) => createProg.mutateAsync(v)} submitLabel="Save" />
      {ctx && <ContextPanel open={!!ctx} onOpenChange={(o) => !o && setCtx(null)} customerId={ctx.customerId} petId={ctx.petId} vertical="training" />}
    </div>
  );
}
